import React, { useEffect, useState } from "react";
import axios from "axios";

// Caché en memoria (por sesión de la pestaña) de ficheros ya descargados:
// evita volver a pedir la misma foto cada vez que se re-renderiza una lista.
const cache = new Map(); // url -> Promise<objectURL>

// Descarga un fichero protegido (tickets, fotos de entrega/servicio/gastos,
// sustitutivos) CON el token de sesión y devuelve una URL local (blob:) que se
// puede usar en <img>, window.open o una descarga. Desde la auditoría de
// seguridad del 24/09/2026 estos ficheros ya no son públicos: un <img src> o un
// enlace normal no manda el token y el servidor responde 401.
export function obtenerBlobUrl(url) {
  if (!url) return Promise.resolve(null);
  if (/^(blob:|data:)/.test(url)) return Promise.resolve(url);
  // Imágenes de otras webs (p.ej. fotos de producto del supermercado): no son
  // nuestras ni llevan token; se usan tal cual.
  try {
    const u = new URL(url, window.location.origin);
    if (u.origin !== window.location.origin) return Promise.resolve(url);
  } catch (e) { /* url relativa rara: se intenta descargar */ }
  if (!cache.has(url)) {
    const p = axios
      .get(url, { responseType: "blob" })
      .then((res) => URL.createObjectURL(res.data))
      .catch((e) => {
        cache.delete(url);
        throw e;
      });
    cache.set(url, p);
  }
  return cache.get(url);
}

// Abre un fichero protegido en una pestaña nueva. La pestaña se abre ANTES de
// la descarga (dentro del clic) para que el navegador no la bloquee como popup.
export async function abrirFicheroProtegido(url) {
  const win = window.open("", "_blank");
  try {
    const blobUrl = await obtenerBlobUrl(url);
    if (win) win.location.href = blobUrl;
    else window.location.href = blobUrl;
  } catch (e) {
    if (win) win.close();
    alert("No se pudo abrir el archivo");
  }
}

// Como fetch(url) pero con el token: devuelve un objeto con .ok y .blob() para
// poder sustituir fetch en el código de descarga existente sin reescribirlo.
export async function fetchProtegido(url) {
  const res = await axios.get(url, { responseType: "blob" });
  return { ok: true, status: res.status, blob: async () => res.data };
}

// Descarga un fichero protegido con el nombre indicado.
export async function descargarFicheroProtegido(url, nombre) {
  try {
    const blobUrl = await obtenerBlobUrl(url);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = nombre || url.split("/").pop() || "archivo";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch (e) {
    alert("No se pudo descargar el archivo");
  }
}

// <img> que carga la imagen con el token. Acepta las mismas props que <img>.
// Mientras carga no muestra nada (o `placeholder` si se pasa).
export default function AuthImage({ src, alt = "", placeholder = null, onError, ...props }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let vivo = true;
    setError(false);
    setBlobUrl(null);
    obtenerBlobUrl(src)
      .then((u) => { if (vivo) setBlobUrl(u); })
      .catch(() => { if (vivo) setError(true); });
    return () => { vivo = false; };
  }, [src]);

  useEffect(() => {
    if (error && onError) onError();
  }, [error, onError]);

  if (!blobUrl) return placeholder;
  return <img src={blobUrl} alt={alt} onError={onError} {...props} />;
}
